// shared/helpers.ts
import { environment } from '../../environments/environment';

/**
 * Validate email format.
 * @param email - Email string to validate
 * @returns true if valid, false otherwise
 */
export function isValidEmail(email: string): boolean {
  if (!email) return false;

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Revert invalid email input to previous valid value.
 * @param value - Current input value
 * @param previous - Previous valid value
 * @returns the corrected value (either current if valid, or previous if invalid)
 */
export function sanitizeEmailInput(value: string, previous: string): string {
  return isValidEmail(value) || value === '' ? value : previous;
}
/*
 * @param name - Name string to validate
 * @param maxLength - Maximum allowed length (default 50)
 * @returns true if valid, false otherwise
 */
export function isValidName(name: string, maxLength = 50): boolean {
  if (!name) return false;
  // Only letters, spaces, hyphens, apostrophes allowed
  const nameRegex = /^[a-zA-Z\s'-]+$/;
  return nameRegex.test(name) && name.length <= maxLength;
}

/**
 * Revert invalid name input to previous valid value.
 * @param value - Current input value
 * @param previous - Previous valid value
 * @param maxLength - Maximum allowed length (default 50)
 * @returns the corrected value (either current if valid, or previous if invalid)
 */
export function sanitizeNameInput(value: string, previous: string, maxLength = 50): string {
  return isValidName(value, maxLength) || value === '' ? value : previous;
}

/**
 * Validate company name.
 * @param name - Company name string
 * @param maxLength - Maximum allowed length (default 100)
 * @returns true if valid, false otherwise
 */
export function isValidCompanyName(name: string, maxLength = 100): boolean {
  if (!name) return false;
  // Allow letters, numbers, spaces, hyphens, periods, &, commas
  const companyRegex = /^[a-zA-Z0-9\s\-.,&']+$/;
  return companyRegex.test(name) && name.length <= maxLength;
}

/**
 * Revert invalid company name input to previous valid value.
 * @param value - Current input
 * @param previous - Previous valid value
 * @param maxLength - Max length (default 100)
 * @returns corrected value
 */
export function sanitizeCompanyNameInput(value: string, previous: string, maxLength = 100): string {
  return isValidCompanyName(value, maxLength) || value === '' ? value : previous;
}

/**
 * Validate mobile number.
 * @param number - Mobile number string
 * @param maxLength - Maximum allowed length (default 15)
 * @returns true if valid, false otherwise
 */
export function isValidMobile(number: string, maxLength = 15): boolean {
  if (!number) {
    return false;
  }
  // Allow optional +, digits, spaces, hyphens
  const mobileRegex = /^\+?[0-9\s-]+$/;
  // Count digits only
  const digitsOnly = number.replace(/\D/g, '');
  return mobileRegex.test(number) && digitsOnly.length <= maxLength;
}

/**
 * Revert invalid mobile input to previous valid value.
 * @param value - Current input
 * @param previous - Previous valid value
 * @param maxLength - Maximum digits (default 15)
 * @returns corrected value
 */
export function sanitizeMobileInput(value: string, previous: string, maxLength = 15): string {
  return isValidMobile(value, maxLength) || value === '' ? value : previous;
}

export const getEnvName = (): string => {
  return environment?.envName ?? '';
};